import {faBalanceScale, faTimes} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Form, Radio, RadioChangeEvent, Select, Space} from 'antd';
import dayjs from 'dayjs';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {AdditionalInfo} from '../../../../../../layout/additional-info/additional-info';
import {Locale} from '../../../../../../provider/locale/locale';
import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {AnalyticsTarget, track} from '../../../../../../service/analytics/analytics';
import {classNames} from '../../../../../../util/css';
import {DatePeriodEnum, getAnalyticsRangeByPeriodAndDate} from '../../../../../../util/date';
import {CatalogSelect} from '../../../components/drawer-filter/catalog-select/catalog-select';
import {DrawerFilter} from '../../../components/drawer-filter/drawer-filter';
import {UtcRangePicker} from '../../../components/drawer-filter/utc-range-picker/utc-range-picker';
import {dayjsRangeToCustomRange} from '../../../components/drawer-filter/utc-range-picker/utc-range-picker-helper';
import {CustomRangeType} from '../../../components/drawer-filter/utc-range-picker/utc-range-picker-type';
import {getAggregationFromUrl, rangeDisabledDate} from '../../../reviews-analysis-helper';
import {useReviewsAnalysisFilter} from '../../../reviews-analysis-hook';
import {ReviewsAnalysisFilterEnum, ReviewsAnalysisFilterType} from '../../../reviews-analysis-type';
import {KeywordsAndTopicsPageContext} from '../keywords-and-topics-page-context';
import {KeywordsAndTopicsTabsEnum} from '../keywords-and-topics-page-type';

import {PERIOD_OPTIONS, REVIEWS_ANALYTICS_DELAY_DAYS} from './keywords-and-topics-const';
import {getDateAndPeriodForAnalysis} from './keywords-and-topics-form-helper';
import {KeywordsAndTopicsFormType, KeywordsAndTopicsFormTypeAggregationEnum} from './keywords-and-topics-type';
import * as styles from './keywords-and-topics-form.scss';

type PropsType = {
    withAggregation?: boolean;
    activeTab?: KeywordsAndTopicsTabsEnum;

    isCompareRangeAllowed?: boolean;
    isTopicsAllowed?: boolean;
};

export function KeywordsAndTopicsForm(props: PropsType): JSX.Element {
    const {withAggregation, isTopicsAllowed, isCompareRangeAllowed, activeTab} = props;

    const [form] = Form.useForm<KeywordsAndTopicsFormType>();
    const {filter, setFilter} = useReviewsAnalysisFilter();
    const {getLocalizedString} = useLocale();

    const {isFormInProgress} = useContext(KeywordsAndTopicsPageContext);

    const lastAvailableAnalyticsDate = useMemo(
        () => dayjs().subtract(REVIEWS_ANALYTICS_DELAY_DAYS, 'day').toDate(),
        []
    );

    const compareAllowed = useMemo(() => {
        return isCompareRangeAllowed && Boolean(filter[ReviewsAnalysisFilterEnum.CompareRange][0]);
    }, [filter, isCompareRangeAllowed]);

    const [periodOption, setPeriodOption] = useState(
        filter[ReviewsAnalysisFilterEnum.Range][0] ? DatePeriodEnum.Custom : DatePeriodEnum.Month
    );

    const [aggregationOption, setAggregationOption] = useState(
        getAggregationFromUrl(filter[ReviewsAnalysisFilterEnum.Aggregation]) ||
            KeywordsAndTopicsFormTypeAggregationEnum.Day
    );

    useEffect(() => {
        if (!filter[ReviewsAnalysisFilterEnum.Range][0]) {
            const defaultRange = getAnalyticsRangeByPeriodAndDate(lastAvailableAnalyticsDate, DatePeriodEnum.Month);

            setFilter({
                [ReviewsAnalysisFilterEnum.Range]: [defaultRange.start, defaultRange.end],
            });
        }
    }, [filter, lastAvailableAnalyticsDate, setFilter]);

    const handlePeriodOptionsChange = useCallback(
        (event: RadioChangeEvent) => {
            const newOption = PERIOD_OPTIONS.find((option) => option.value === event.target.value);

            if (newOption?.value && newOption.value !== DatePeriodEnum.Custom) {
                setPeriodOption(newOption?.value);

                const newDateRange = getAnalyticsRangeByPeriodAndDate(lastAvailableAnalyticsDate, newOption.value);
                const newCompareDateRange = getAnalyticsRangeByPeriodAndDate(newDateRange.start, newOption.value);

                setFilter({
                    [ReviewsAnalysisFilterEnum.Range]: dayjsRangeToCustomRange([
                        dayjs(newDateRange.start),
                        dayjs(newDateRange.end),
                    ]),
                    ...(compareAllowed
                        ? {
                              [ReviewsAnalysisFilterEnum.CompareRange]: dayjsRangeToCustomRange([
                                  dayjs(newCompareDateRange.start),
                                  dayjs(newCompareDateRange.end),
                              ]),
                          }
                        : {}),
                });

                track(
                    AnalyticsTarget.ReviewsAnalysis.AnalyzePeriod,
                    getDateAndPeriodForAnalysis([newDateRange.start, newDateRange.end], newOption.value)
                );
            }
        },
        [compareAllowed, lastAvailableAnalyticsDate, setFilter]
    );

    const handleCatalogChange = useCallback(
        (value: Array<number>) => {
            setFilter({
                [ReviewsAnalysisFilterEnum.Catalogs]: value,
            });
        },
        [setFilter]
    );

    const handlePeriodChange = useCallback(
        (value: CustomRangeType) => {
            setFilter({
                [ReviewsAnalysisFilterEnum.Range]: value,
            });

            setPeriodOption((previous) => {
                const newOption = PERIOD_OPTIONS.find((option) => option.value === DatePeriodEnum.Custom);

                const newPeriod = newOption ? newOption.value : previous;

                if (value[0] && value[1]) {
                    track(AnalyticsTarget.ReviewsAnalysis.AnalyzePeriod, getDateAndPeriodForAnalysis(value, newPeriod));
                }

                return newPeriod;
            });
        },
        [setFilter]
    );

    const handleComparePeriodChange = useCallback(
        (value: CustomRangeType) => {
            setFilter({
                [ReviewsAnalysisFilterEnum.CompareRange]: value,
            });
        },
        [setFilter]
    );

    // Enable comparison by seeding the compare range with the equal-length window
    // immediately preceding the main range; the range picker then shows for fine-tuning.
    const handleCompareEnable = useCallback(() => {
        const [start, end] = filter[ReviewsAnalysisFilterEnum.Range];

        if (!start || !end) {
            return;
        }

        const compareEnd = dayjs(start).subtract(1, 'day');
        const compareStart = compareEnd.subtract(dayjs(end).diff(dayjs(start), 'day'), 'day');

        setFilter({
            [ReviewsAnalysisFilterEnum.CompareRange]: dayjsRangeToCustomRange([compareStart, compareEnd]),
        });
    }, [filter, setFilter]);

    const handleCompareReset = useCallback(() => {
        setFilter({
            [ReviewsAnalysisFilterEnum.CompareRange]: [null, null],
        });
    }, [setFilter]);

    const handleAggregationChange = useCallback(
        (value: KeywordsAndTopicsFormTypeAggregationEnum) => {
            setFilter({
                [ReviewsAnalysisFilterEnum.Aggregation]: value,
            });
            setAggregationOption(value);
        },
        [setFilter]
    );

    useEffect(() => {
        form.resetFields();
    }, [filter, form]);

    return (
        <Form<KeywordsAndTopicsFormType> form={form} initialValues={filter}>
            <div className={styles.KeywordsAndTopicsForm_filters}>
                <div
                    className={classNames(
                        styles.KeywordsAndTopicsForm_filterRow,
                        styles.KeywordsAndTopicsForm_filterRow__spaceBetween
                    )}
                >
                    <div className={styles.KeywordsAndTopicsForm_period}>
                        <Form.Item className={styles.KeywordsAndTopicsForm_periodInput}>
                            <Radio.Group
                                disabled={isFormInProgress}
                                onChange={handlePeriodOptionsChange}
                                optionType="button"
                                value={periodOption}
                            >
                                {PERIOD_OPTIONS.map((option) => (
                                    <Radio key={option.value} value={option.value}>
                                        {option.label}
                                    </Radio>
                                ))}
                            </Radio.Group>
                        </Form.Item>
                        <AdditionalInfo title={<Locale stringKey="REVIEWS_ANALYSIS__FILTER__PERIOD_INFO_TITLE" />}>
                            <Locale stringKey="REVIEWS_ANALYSIS__FILTER__PERIOD_INFO_TEXT" />
                        </AdditionalInfo>

                        {withAggregation && (
                            <Form.Item className={styles.KeywordsAndTopicsForm_periodInput}>
                                <Select
                                    onChange={handleAggregationChange}
                                    optionLabelProp="title"
                                    options={[
                                        {
                                            title: getLocalizedString('REVIEWS_ANALYSIS__FILTER__GROUP_BY__DAY'),
                                            value: KeywordsAndTopicsFormTypeAggregationEnum.Day,
                                            label: <Locale stringKey="REVIEWS_ANALYSIS__FILTER__PERIOD__DAY" />,
                                        },
                                        {
                                            title: getLocalizedString('REVIEWS_ANALYSIS__FILTER__GROUP_BY__WEEK'),
                                            value: KeywordsAndTopicsFormTypeAggregationEnum.Week,
                                            label: <Locale stringKey="REVIEWS_ANALYSIS__FILTER__PERIOD__WEEK" />,
                                        },
                                        {
                                            title: getLocalizedString('REVIEWS_ANALYSIS__FILTER__GROUP_BY__MONTH'),
                                            value: KeywordsAndTopicsFormTypeAggregationEnum.Month,
                                            label: (
                                                <Locale stringKey="REVIEWS_ANALYSIS__FILTER__PERIOD__PREVIOUS_FULL_MONTH" />
                                            ),
                                        },
                                    ]}
                                    value={aggregationOption}
                                />
                            </Form.Item>
                        )}
                    </div>

                    <div className={styles.KeywordsAndTopicsForm_drawerButton}>
                        <DrawerFilter
                            isCompareRangeAllowed={isCompareRangeAllowed}
                            isTopicsAllowed={isTopicsAllowed}
                            isWithTextOnly
                            onSubmit={() => setPeriodOption(DatePeriodEnum.Custom)}
                        />
                    </div>
                </div>

                <Space className={styles.KeywordsAndTopicsForm_filterRow} wrap>
                    <Form.Item<ReviewsAnalysisFilterType>
                        className={classNames(
                            styles.KeywordsAndTopicsForm_input,
                            styles.KeywordsAndTopicsForm_input__period
                        )}
                        name={ReviewsAnalysisFilterEnum.Range}
                    >
                        <UtcRangePicker
                            defaultMode
                            disabled={isFormInProgress}
                            disabledDate={rangeDisabledDate}
                            onChange={handlePeriodChange}
                        />
                    </Form.Item>
                    {isCompareRangeAllowed &&
                        (compareAllowed ? (
                            <>
                                <Form.Item<ReviewsAnalysisFilterType>
                                    className={classNames(
                                        styles.KeywordsAndTopicsForm_input,
                                        styles.KeywordsAndTopicsForm_input__period
                                    )}
                                    name={ReviewsAnalysisFilterEnum.CompareRange}
                                >
                                    <UtcRangePicker
                                        defaultMode
                                        disabled={isFormInProgress}
                                        disabledDate={rangeDisabledDate}
                                        onChange={handleComparePeriodChange}
                                    />
                                </Form.Item>
                                <Button
                                    disabled={isFormInProgress}
                                    icon={<FontAwesomeIcon icon={faTimes} />}
                                    onClick={handleCompareReset}
                                >
                                    <Locale stringKey="TEXT__RESET" />
                                </Button>
                            </>
                        ) : (
                            <Button
                                disabled={isFormInProgress}
                                icon={<FontAwesomeIcon icon={faBalanceScale} />}
                                onClick={handleCompareEnable}
                            >
                                <Locale stringKey="REVIEWS_ANALYSIS__FILTER__COMPARE_PERIODS" />
                            </Button>
                        ))}
                    {activeTab !== KeywordsAndTopicsTabsEnum.Aspects && (
                        <Form.Item<ReviewsAnalysisFilterType>
                            className={styles.KeywordsAndTopicsForm_input}
                            name={ReviewsAnalysisFilterEnum.Catalogs}
                        >
                            <CatalogSelect
                                disabled={isFormInProgress}
                                onChange={handleCatalogChange}
                                placeholder={<Locale stringKey="REVIEWS__FILTER__ALL__SOURCES" />}
                            />
                        </Form.Item>
                    )}
                </Space>
            </div>
        </Form>
    );
}
